import { useEffect, useState } from 'react'

import { useUrlManager } from 'src/hooks/useUrlManager'
import { useIsMobile } from 'src/hooks/useIsMobile'

export type GameViewMode = 'list' | 'calendar' | 'details'
export type CalendarSubView = 'month' | 'week' | 'day' | 'agenda'

export const VIEW_PARAM = 'sn_view'
export const CALENDAR_VIEW_PARAM = 'sn_cal_view'
export const CALENDAR_DATE_PARAM = 'sn_cal_date'

const VIEW_MODES: GameViewMode[] = ['list', 'calendar', 'details']
const CALENDAR_SUB_VIEWS: CalendarSubView[] = ['month', 'week', 'day', 'agenda']

const CIVIL_DATE = /^\d{4}-\d{2}-\d{2}$/

export interface UseGameViewModeArgs {
  /**
   * Whether the host offers Details at all. False on hosts whose dataset the
   * grid cannot serve honestly — the Team page, where the list is one team's
   * games across competitions rather than a competition's schedule.
   *
   * Gates the URL too, not just the tab: without this a `sn_view=details` deep
   * link would render a view the host has no control to leave.
   */
  supportsDetails?: boolean
}

export interface GameViewModeState {
  mode: GameViewMode
  calendarSubView: CalendarSubView
  /** `YYYY-MM-DD` anchoring the calendar viewport, or null for "today". */
  calendarDate: string | null
  /**
   * True when a deep-linked Details view was downgraded for a small screen.
   * Deliberately NOT set when the host simply does not offer Details — there
   * is nothing to explain to someone who never had that view.
   */
  didFallBackFromDetails: boolean
  isMobile: boolean
  setMode: (next: GameViewMode) => void
  setCalendarSubView: (next: CalendarSubView) => void
  setCalendarDate: (next: string | null) => void
}

/**
 * View mode lives in the URL and is DERIVED from it on every render — never
 * mirrored into `useState` seeded from the URL. A `useState` seed only reads the
 * URL once, so Back and Forward would move the address bar while the view stayed
 * put, and a shared link would open on whatever the seed happened to be.
 *
 * `sn_view` pushes (switching views is navigation the user expects Back to
 * undo); the calendar's own sub-view and anchor date replace, because paging
 * through months should not bury the previous page under twelve history entries.
 */
export const useGameViewMode = ({
  supportsDetails = true,
}: UseGameViewModeArgs = {}): GameViewModeState => {
  'use memo'

  const { currentQueryParams, setParams } = useUrlManager()
  const isMobile = useIsMobile()

  const rawMode = currentQueryParams.get(VIEW_PARAM)
  const rawSubView = currentQueryParams.get(CALENDAR_VIEW_PARAM)
  const rawDate = currentQueryParams.get(CALENDAR_DATE_PARAM)

  const parsedMode: GameViewMode | null =
    rawMode === null || rawMode === ''
      ? 'list'
      : VIEW_MODES.includes(rawMode as GameViewMode)
      ? (rawMode as GameViewMode)
      : null

  //  Details is a wide spreadsheet with inline editing; there is no honest way
  //  to render it at phone width, so a deep link degrades to List and says so.
  const detailsBlocked = isMobile && parsedMode === 'details'
  //  A host that does not offer Details drops the same link silently. Kept
  //  separate from `detailsBlocked` so only the mobile case shows the notice:
  //  "we shrank this for your screen" is worth saying, "this view does not
  //  exist here" is not.
  const detailsUnsupported = !supportsDetails && parsedMode === 'details'
  const mode: GameViewMode =
    detailsBlocked || detailsUnsupported ? 'list' : parsedMode ?? 'list'

  const parsedSubView: CalendarSubView | null =
    rawSubView !== null &&
    CALENDAR_SUB_VIEWS.includes(rawSubView as CalendarSubView)
      ? (rawSubView as CalendarSubView)
      : null

  //  Month and week columns cannot survive a phone viewport either — agenda is
  //  the one calendar layout that reads as a list.
  const calendarSubView: CalendarSubView = isMobile
    ? 'agenda'
    : parsedSubView ?? 'month'

  const calendarDate =
    rawDate !== null && CIVIL_DATE.test(rawDate) ? rawDate : null

  //  Latched, because the canonicalizer strips `sn_view=details` from the URL on
  //  the very next tick — reading `detailsBlocked` directly would flash the
  //  notice for one render and then hide it before anyone could read it.
  const [didFallBackFromDetails, setDidFallBackFromDetails] = useState(false)

  useEffect(() => {
    if (detailsBlocked) {
      setDidFallBackFromDetails(true)
    }
  }, [detailsBlocked])

  //  Canonicalize junk in the address bar so a bad link settles on something
  //  real, without adding a history entry the user did not create.
  useEffect(() => {
    const updates: Record<string, string | null> = {}

    if (parsedMode === null) {
      updates[VIEW_PARAM] = null
    }
    if (detailsBlocked || detailsUnsupported) {
      updates[VIEW_PARAM] = null
    }
    if (rawSubView !== null && parsedSubView === null) {
      updates[CALENDAR_VIEW_PARAM] = null
    }
    if (rawDate !== null && calendarDate === null) {
      updates[CALENDAR_DATE_PARAM] = null
    }

    if (Object.keys(updates).length > 0) {
      setParams(updates, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    parsedMode,
    detailsBlocked,
    detailsUnsupported,
    rawSubView,
    parsedSubView,
    rawDate,
    calendarDate,
  ])

  const setMode = (next: GameViewMode) => {
    //  An explicit choice clears the fallback notice — it was about the link the
    //  user arrived on, not about where they are going now.
    setDidFallBackFromDetails(false)
    if (next === 'list') {
      //  ONE history operation: clearing three params with three `setParam`
      //  calls would leave three entries between the user and where they were.
      setParams(
        {
          [VIEW_PARAM]: null,
          [CALENDAR_VIEW_PARAM]: null,
          [CALENDAR_DATE_PARAM]: null,
        },
        { replace: false }
      )
      return
    }
    setParams({ [VIEW_PARAM]: next }, { replace: false })
  }

  const setCalendarSubView = (next: CalendarSubView) => {
    setParams({ [CALENDAR_VIEW_PARAM]: next }, { replace: true })
  }

  const setCalendarDate = (next: string | null) => {
    setParams({ [CALENDAR_DATE_PARAM]: next }, { replace: true })
  }

  return {
    mode,
    calendarSubView,
    calendarDate,
    didFallBackFromDetails,
    isMobile,
    setMode,
    setCalendarSubView,
    setCalendarDate,
  }
}
